import { trackError } from '@iheartjane/tracking';
import { Storage } from '@iheartjane/utils';
import type { Reducer } from '@reduxjs/toolkit';
import { createAction } from '@reduxjs/toolkit';
import type { DeepReadonly } from 'ts-essentials';

/* eslint-disable-next-line no-restricted-imports */
import type { PosCart } from '@jane/shared/models';
import type { Address } from '@jane/shared/types';

import { PosCartSource } from '../../sources/posCart';
import type { CustomerAction, CustomerThunkAction } from '../customerAction';
import type { AppState } from '../redux';

export const GET_POS_CART = 'pos-cart/get';
const initiateGetPosCart = createAction<void, typeof GET_POS_CART>(
  GET_POS_CART
);

export const GET_POS_CART_SUCCESS = 'pos-cart/get-success';
export const getPosCartSuccess = createAction<
  { pos_cart: PosCart },
  typeof GET_POS_CART_SUCCESS
>(GET_POS_CART_SUCCESS);

export const GET_POS_CART_ERROR = 'pos-cart/get-error';
export const getPosCartError = createAction<unknown, typeof GET_POS_CART_ERROR>(
  GET_POS_CART_ERROR
);

export const getPosCart = (): CustomerThunkAction => (dispatch, getState) => {
  const {
    cart: {
      cart: { uuid, id, products, store },
    },
  } = getState();

  dispatch(initiateGetPosCart());

  const deliveryAddress = Storage.get<Address | null>('deliveryAddress');

  if (!uuid) {
    trackError('Cannot fetch PosCart without a cart uuid', {
      uuid,
      id,
      productCount: products.length,
      storeId: store?.id,
    });

    return;
  }

  return PosCartSource.get(deliveryAddress, uuid).then(
    (result) => dispatch(getPosCartSuccess(result)),
    (err) => dispatch(getPosCartError(err))
  );
};

export type PosCartActions =
  | ReturnType<typeof initiateGetPosCart>
  | ReturnType<typeof getPosCartSuccess>
  | ReturnType<typeof getPosCartError>;

export type PosCartState = DeepReadonly<{
  isLoading: boolean;
  posCart: PosCart;
}>;

const getInitialState = (): PosCartState => ({
  posCart: {
    totals: null,
  },
  isLoading: false,
});

export const posCartReducer: Reducer<PosCartState, CustomerAction> = (
  state = getInitialState(),
  action
) => {
  switch (action.type) {
    case GET_POS_CART:
      return { ...state, isLoading: true };

    case GET_POS_CART_SUCCESS:
      return { ...state, posCart: action.payload.pos_cart, isLoading: false };

    case GET_POS_CART_ERROR:
      return { ...state, isLoading: false };
  }

  return state;
};

export const selectPosCart = (state: AppState) => state.posCart;
